<template>
  <v-container fluid>
    <v-row>
      <v-col class="text-h3 gray--text">
        Hipótese Diagnóstica
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <QuerySearchDropdown
          v-model="cid"
          :query="getCidsQuery"
          queryKey="cids"
          label="CID"
          item-text="codeDescription"
          input-prompt-text="Digite o nome ou código para pesquisar..."
          not-found-text="Nenhum CID encontrado."
          :error-messages="errors.cid"
          :disabled="attendanceEsigned"
        />
      </v-col>

      <v-col>
        <v-combobox
          v-model="subCid"
          outlined
          dense
          hide-details
          label="Sub CID"
          :items="subCids"
          item-text="description"
          background-color="white"
          :disabled="attendanceEsigned"
        />
      </v-col>

      <v-col>
        <v-combobox
          v-model="diagnosticHypothesisSite"
          outlined
          dense
          hide-details
          label="Local"
          :items="diagnosticHypothesisSites"
          item-text="name"
          background-color="white"
          :disabled="attendanceEsigned"
        />
      </v-col>
    </v-row>

    <v-row dense>
      <v-col>
        <v-textarea
          v-model="description"
          v-uppercase
          outlined
          dense
          hide-details
          rows="3"
          label="Descrição"
          background-color="white"
          :disabled="attendanceEsigned"
        />
      </v-col>
    </v-row>

    <v-row dense>
      <v-col>
        <v-btn
          :dark="!attendanceEsigned"
          color="light-blue"
          @click="saveDiagnosticHypothesis"
          :disabled="attendanceEsigned"
        >
          Adicionar
        </v-btn>
      </v-col>
    </v-row>

    <v-row v-if="activeDiagnosticHypotheses.length > 0">
      <v-col>
        <h3>
          CIDs ativas
        </h3>
      </v-col>
    </v-row>

    <v-row>
      <v-col
        v-for="diagnosticHypothesis in activeDiagnosticHypotheses"
        :key="diagnosticHypothesis.id"
        cols="12"
      >
        <DiagnosticHypothesis
          :attendance="attendance"
          :diagnosticHypothesis="diagnosticHypothesis"
          :disabled="attendanceEsigned"
        />
      </v-col>
    </v-row>

    <v-row v-if="inactiveDiagnosticHypotheses.length > 0">
      <v-col>
        <h3>
          CIDs inativas
        </h3>
      </v-col>
    </v-row>

    <v-row>
      <v-col
        v-for="diagnosticHypothesis in inactiveDiagnosticHypotheses"
        :key="diagnosticHypothesis.id"
        cols="12"
      >
        <DiagnosticHypothesis
          :attendance="attendance"
          :diagnosticHypothesis="diagnosticHypothesis"
          :disabled="attendanceEsigned"
        />
      </v-col>
    </v-row>

    <v-row v-if="discartedDiagnosticHypotheses.length > 0">
      <v-col>
        <h3>
          CIDs descartadas
        </h3>
      </v-col>
    </v-row>

    <v-row>
      <v-col
        v-for="diagnosticHypothesis in discartedDiagnosticHypotheses"
        :key="diagnosticHypothesis.id"
        cols="12"
      >
        <DiagnosticHypothesis
          :attendance="attendance"
          :diagnosticHypothesis="diagnosticHypothesis"
          :disabled="attendanceEsigned"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  import GET_DIAGNOSTIC_HYPOTHESIS_SITES from "@/graphql/diagnostic_hypothesis_sites.gql";
  import GET_DIAGNOSTIC_HYPOTHESES from "@/graphql/diagnostic_hypotheses.gql";
  import SAVE_DIAGNOSTIC_HYPOTHESIS from "@/graphql/save_diagnostic_hypothesis.gql";

  import QuerySearchDropdown from "@/components/ui/form/query_search_dropdown.vue";
  import DiagnosticHypothesis from "@/components/medical_record/hd/diagnostic_hypothesis.vue";

  export default {
    name: "DiagnosticHypotheses",
    components: {
      QuerySearchDropdown,
      DiagnosticHypothesis,
    },
    props: {
      attendance: Object,
      attendanceEsigned: Boolean,
    },
    data() {
      return {
        getCidsQuery: require("@/graphql/cids.gql"),
        cid: "",
        subCid: "",
        diagnosticHypothesisSite: "",
        description: "",
        errors: {},
      };
    },
    watch: {
      cid() {
        this.subCid = "";
      },
    },
    apollo: {
      diagnosticHypothesisSites: {
        query: GET_DIAGNOSTIC_HYPOTHESIS_SITES,
      },
      diagnosticHypotheses: {
        query: GET_DIAGNOSTIC_HYPOTHESES,
        variables() {
          return {
            attendanceId: this.attendance.id,
          };
        },
        pollInterval: 20000,
      },
    },
    computed: {
      subCids() {
        if (this.cid) {
          return this.cid.cidItems;
        }
        return [];
      },
      activeDiagnosticHypotheses() {
        return this.lodash.filter(this.diagnosticHypotheses, { currentStatus: "active" });
      },
      inactiveDiagnosticHypotheses() {
        return this.lodash.filter(this.diagnosticHypotheses, { currentStatus: "inactive" });
      },
      discartedDiagnosticHypotheses() {
        return this.lodash.filter(this.diagnosticHypotheses, { currentStatus: "discarted" });
      },
    },
    methods: {
      validate() {
        const errorMsg = "Selecione um CID válido";
        this.errors = {};

        if (!this.cid?.id) {
          this.errors.cid = errorMsg;
        }

        if (Object.keys(this.errors).length === 0) {
          return true;
        }

        return false;
      },
      saveDiagnosticHypothesis() {
        if (this.attendanceEsigned) return;
        if (!this.validate()) return;

        let cidItemId = null;
        if (this.subCid) {
          cidItemId = this.subCid.id;
        }

        let diagnosticHypothesisSiteId = null;
        if (this.diagnosticHypothesisSite) {
          diagnosticHypothesisSiteId = this.diagnosticHypothesisSite.id;
        }

        this.apolloMutate({
          mutation: SAVE_DIAGNOSTIC_HYPOTHESIS,
          variables: {
            attendanceId: this.attendance.id,
            cidId: this.cid.id,
            cidItemId: cidItemId,
            diagnosticHypothesisSiteId: diagnosticHypothesisSiteId,
            description: this.description,
          },
        }).then(() => {
          this.cid = null;
          this.subCid = null;
          this.diagnosticHypothesisSite = null;
          this.description = "";

          this.$apollo.queries.diagnosticHypotheses.refetch();
        });
      },
    },
  };
</script>

<style lang="scss" scoped></style>
